import React, {FC, ReactNode} from 'react';
import {animated, SpringValue, SpringValues, TransitionFn, useTransition} from 'react-spring';
import styled from 'styled-components';

import {CenteredPopover} from '../../../../libs/shared/ui/src/components/popovers/centeredPopover';
import {onboardingGuideSlideHeight, onboardingGuideSlideWidth} from './onboardingGuideHelpers';
import {OnboardingGuidesEnum} from './onboardingGuideTypes';

/*
 * Constants.
 */

export const onboardingGuidePopoverAnimationMs = 150;

/*
 * Types.
 */

type OnboardingGuideAnimatedProps = {opacity: number};
export type OnboardingGuideAnimatedStyles = SpringValues<OnboardingGuideAnimatedProps>;
export type OnboardingGuideRenderer = (styles: OnboardingGuideAnimatedStyles) => ReactNode;

/*
 * Props.
 */

interface OnboardingGuidePopoverProps {
  children: ReactNode;
  onRequestClose?: () => void;
  styles?: {
    opacity: SpringValue<number>;
  };
  width?: string;
  height?: string;
}

/*
 * Style.
 */

const StyledOnboardingGuideContainerDiv = styled.div<{
  $width: string | undefined;
  $height: string | undefined;
}>`
  display: flex;
  border-radius: 16px;
  width: ${(p) => p.$width ?? onboardingGuideSlideWidth};
  height: ${(p) => p.$height ?? onboardingGuideSlideHeight};
  overflow: hidden;
  box-shadow:
    0px 3px 10px 0px rgba(0, 0, 0, 0.1),
    0px 0px 1px 0px rgba(0, 0, 0, 0.3);
  background-color: ${(p) => p.theme.greys.white};
`;

const AnimatedCenteredPopover = animated(CenteredPopover);

/*
 * Component.
 */

export const OnboardingGuidePopover: FC<OnboardingGuidePopoverProps> = ({
  children,
  onRequestClose,
  styles,
  width,
  height,
}) => (
  <AnimatedCenteredPopover onRequestClose={onRequestClose} style={styles}>
    <StyledOnboardingGuideContainerDiv $width={width} $height={height}>
      {children}
    </StyledOnboardingGuideContainerDiv>
  </AnimatedCenteredPopover>
);

/*
 * Helpers.
 */

export function useOnboardingGuideTransition(
  visibleGuide: OnboardingGuidesEnum | undefined,
): TransitionFn<OnboardingGuidesEnum | undefined, OnboardingGuideAnimatedProps> {
  return useTransition(visibleGuide, {
    from: {opacity: 0},
    enter: {opacity: 1},
    leave: {opacity: 0},
    exitBeforeEnter: true,
    config: (item) => (item === undefined ? {duration: 0} : {duration: onboardingGuidePopoverAnimationMs}),
  });
}
